/**
 * Auth Service - Abstraction layer for authentication
 * This service provides a single source of truth for auth operations.
 * Uses Auth0 for authentication (migrated from Netlify Identity).
 * 
 * This is a non-React service that can be used anywhere in the app.
 * React components should use AuthContext instead.
 */

// Callback functions that can be registered by AuthContext
let logoutCallback = null;
let getTokenCallback = null;
let getCurrentUserCallback = null;

/**
 * Register logout callback (called by AuthContext)
 */
export const registerLogoutCallback = (callback) => {
  logoutCallback = callback;
};

/**
 * Register getToken callback (called by AuthContext)
 */
export const registerGetTokenCallback = (callback) => {
  getTokenCallback = callback;
};

/**
 * Register getCurrentUser callback (called by AuthContext)
 */
export const registerGetCurrentUserCallback = (callback) => {
  getCurrentUserCallback = callback;
};

/**
 * Get authentication token
 * Uses callback if registered, otherwise returns null
 * 
 * Note: With Auth0, this may return a Promise, so callers should handle both sync and async cases
 * 
 * Components must wait for authReady before making API calls.
 * No fallback - if this returns null, it means auth isn't ready yet.
 */
export const getAuthToken = () => {
  if (getTokenCallback) {
    const result = getTokenCallback();
    // If callback returns a Promise (Auth0), return it
    // If it returns a string (legacy), return it
    return result;
  }
  
  // No callback registered - return null
  // This should only happen if AuthContext hasn't initialized yet
  return null;
};

/**
 * Get current user
 * Uses callback if registered, otherwise returns null
 */
export const getCurrentUser = () => {
  if (getCurrentUserCallback) {
    return getCurrentUserCallback();
  }
  
  // No callback registered - return null
  return null;
};

/**
 * Logout user
 * Uses callback if registered, otherwise does nothing
 */
export const logout = () => {
  if (logoutCallback) {
    logoutCallback();
  } else {
    console.warn('Logout called but no callback registered');
  }
};

